import React, { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from 'src/contexts';
import { useHttp } from 'src/hooks';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import LoadingSpinner from 'src/components/common/ui/loader/LoadingSpinner';

interface Props {
  onConnectionChange: (isConnected: boolean) => void;
}

const ConnectFacebookToInstagram: React.FC<Props> = ({ onConnectionChange }) => {
  const { sendRequest, isLoading } = useHttp();
  const { accessToken, profile, fetchProfile } = useContext(AuthContext);
  const [pages, setPages] = useState<
    {
      id: string;
      picture?: string;
      name?: string;
      username?: string;
      accessToken: string;
      instagramLinked?: boolean;
      instagramUsername?: string;
      instagramName?: string;
      instagramPicture?: string;
    }[]
  >([]);
  const [selectedPage, setSelectedPage] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getUserPages = () => {
    sendRequest(
      {
        url: `facebook/user-pages`,
        method: 'GET',
        accessToken,
      },
      (data: any) => {
        if (data?.data?.length > 0) {
          const formattedPages = data.data.map((page: any) => ({
            id: page.id,
            picture: page.picture,
            name: page.name,
            username: page.fbPageUsername || '',
            accessToken : page.accessToken,
            instagramLinked: page.instagramLinked,
            instagramUsername: page.instagramUsername,
            instagramName: page.instagramName,
            instagramPicture: page.instagramPicture,
          }));

          setPages(formattedPages);

          if (formattedPages[0]?.instagramLinked) {
            onConnectionChange(true);
          } else {
            onConnectionChange(false);
          }
        }
      },
    );
  };

  useEffect(() => {
    onConnectionChange(pages[selectedPage]?.instagramLinked || false);
  }, [selectedPage]);

  // const handleInstagramConnect = () => {
  //   sendRequest(
  //     {
  //       url: `instagram/login`,
  //       accessToken,
  //     },
  //     (data: string | any) => {
  //       if (data) {
  //         window.location.href = data;
  //       }
  //     },
  //   );
  // };
  const handleInstagramConnect = (pageId: string, pageAccessToken: string) => {
    sendRequest(
      {
        url: `facebook/pages/pbia`,
        method: 'POST',
        accessToken,
        payload: { pageId, pageAccessToken },
      },
      (data: string | any) => {
        if (data) {
          getUserPages();
        }
      },
    );
  };

  useEffect(() => {
    if (accessToken) getUserPages();
  }, [accessToken]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  return (
    <>
      <div className=" my-8 p-6 border border-[#E5E7E7] rounded-2xl">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-[28px] text-center font-semibold font-inter text-black mb-8">
            Connect Facebook page to Instagram
          </h2>

          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <div className=" flex flex-col items-center space-y-6 border border-[#E5E7E7] rounded-2xl p-6 relative">
              {/* Facebook Page */}
              <div className="flex flex-col items-center relative" ref={dropdownRef}>
                <div
                  className="flex items-center space-x-2 cursor-pointer"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <img
                    src={
                      pages[selectedPage]?.picture ||
                      '../../../../public/assets/images/no-profile.jpg'
                    }
                    alt="Facebook Page"
                    className="rounded-full w-14 h-14"
                  />
                  {pages.length > 1 && <ChevronDownIcon className="w-6 h-6 text-gray-500" />}
                </div>
                <h3 className="text-lg font-semibold text-black mt-2">
                  {pages[selectedPage]?.name || ''}
                </h3>
                {pages[selectedPage]?.username && (
                  <p className="text-sm text-gray-600">@{pages[selectedPage]?.username}</p>
                )}

                {/* Horizontal Dropdown for switching pages */}
                {dropdownOpen && (
                  <div className="absolute top-16 left-0 bg-white border border-gray-300 rounded-md shadow-lg z-10 w-max px-4 py-2 flex space-x-4 overflow-x-auto">
                    {pages.map((page, index) => (
                      <div
                        key={page.id}
                        className="flex flex-col items-center cursor-pointer hover:bg-gray-100 p-2 rounded-md"
                        onClick={() => {
                          setSelectedPage(index);
                          setDropdownOpen(false);
                        }}
                      >
                        <img
                          src={page.picture || '../../../../public/assets/images/no-profile.jpg'}
                          alt="Page"
                          className="w-10 h-10 rounded-full"
                        />
                        <span className="text-xs mt-1">{page.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Connection Icon */}
              <div className="flex items-center justify-center">
                <div className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6 text-indigo-500"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 8.25v7.5m3.75-3.75h-7.5m11.25-7.5v15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 19.5v-15A2.25 2.25 0 015.25 2.25h11.25a2.25 2.25 0 012.25 2.25z"
                    />
                  </svg>
                </div>
              </div>

              {/* Instagram Page */}
              <div className="flex flex-col items-center">
                {pages[selectedPage]?.instagramLinked ? (
                  <>
                    <img
                      src={
                        pages[selectedPage]?.instagramPicture ||
                        '../../../../public/assets/images/no-profile.jpg'
                      }
                      alt="Instagram Page"
                      className="rounded-full w-14 h-14"
                    />
                    <h3 className="text-lg font-semibold text-black mt-2">
                      {pages[selectedPage]?.instagramName || ''}
                    </h3>
                    <p className="text-sm text-gray-600">
                      @{pages[selectedPage]?.instagramUsername}
                    </p>
                  </>
                ) : profile?.instagram ? (
                  <>
                    <img
                      src={
                        profile?.instagram?.profilePictureUrl ||
                        '../../../../public/assets/images/no-profile.jpg'
                      }
                      alt="Instagram Profile"
                      className="rounded-full w-14 h-14"
                    />
                    <h3 className="text-lg font-semibold text-black mt-2">
                      {profile?.instagram?.name || ''}
                    </h3>
                    <p className="text-sm text-gray-600">@{profile?.instagram?.username}</p>
                  </>
                ) : (
                  <button
                    className="mt-4 px-6 py-2 bg-indigo-500 text-white rounded-full"
                    onClick={() =>
                      handleInstagramConnect(
                        pages[selectedPage].id,
                        pages[selectedPage].accessToken,
                      )
                    }
                  >
                    Connect Instagram
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ConnectFacebookToInstagram;
